<script>
    import * as THREE from "three";
    import { onMount } from "svelte";
    import Controls from "../misc/controls/Controls";
    import CityScene from "../scenes/CityScene";
    import AnimatedScene from "../scenes/AnimatedScene";
    import PolesScene from "../scenes/PolesScene";
    import HouseScene from "../scenes/HouseScene";
    import Lights from "../misc/Lights";
    import Teleporter from "../misc/Teleporter";
    import waitForProps from "../utils/WaitForProps";
    import { RGBELoader } from "../misc/RGBELoader";
    import LoadingAnimation from "./LoadingAnimation.svelte";
    import { TWEEN } from "three/examples/jsm/libs/tween.module.min";
    import Icon from "../misc/Icon";
    import { Scene, Renderer, SceneControls, Camera } from "../misc/SceneStore";
    import {
        subscribers,
        houseText,
        appPrimaryColor,
        animatedText,
    } from "../Store";
    import Disney from "../assets/disney.png";
    import Netflix from "../assets/netflix.png";
    import Prime from "../assets/prime.png";
    import Pivot from "../assets/pivot_logo.png";
    import Echo from "../assets/echo5G_logo.png";

    let sceneContainer,
        canvas,
        loadedBG,
        loadedAnimation,
        loadedHouse,
        loadedCity,
        loaded;

    let skyTexture,
        colorSky = new THREE.Color(0xbde0f3);

    async function loadScene() {
        const scene = new THREE.Scene();
        scene.background = colorSky;
        scene.environment = colorSky;

        const camera = new THREE.PerspectiveCamera();
        camera.far = 50;
        const rig = new THREE.Object3D();
        const renderer = loadRenderer();
        const controls = loadControls({ scene, camera, renderer, rig });

        // renderer.toneMapping = THREE.ACESFilmicToneMapping;
        // renderer.toneMappingExposure = 0.6;

        renderer.outputEncoding = THREE.sRGBEncoding;
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        Scene.set(scene);
        Camera.set(camera);
        Renderer.set(renderer);
        SceneControls.set(controls);

        camera.position.set(0, 1.6, 0);
        rig.add(camera);
        scene.add(rig);

        const lights = loadLights(scene, controls);

        loadPoleScene(scene, controls);
        loadCityScene(scene, controls);
        loadAnimatedScene(scene, controls);
        loadHouseScene(scene, controls);
        loadSky(
            scene,
            "https://pivotal-5g-webxr.s3.amazonaws.com/assets/street.hdr"
        );
        loadTeleporters(controls, rig, lights);
    }

    async function loadSky(scene, src) {
        const loader = new RGBELoader();
        loader.load(src, (texture) => {
            texture.mapping = THREE.EquirectangularReflectionMapping;
            skyTexture = texture;
            // scene.background = texture;
            // scene.enviornment = texture;
            loadedBG = true;
        });
    }

    async function loadTeleporters(controls, rig, lights) {
        //city
        const teleportToCity = new Teleporter(
            controls,
            new THREE.Vector3(0, 0, 0.5),
            rig,
            appPrimaryColor
        );
        const cityIcon = new Icon(controls, {
            src: "https://pivotal-5g-webxr.s3.amazonaws.com/assets/cityGrid.png",
            color: "white",
            height: 0.5,
            width: 0.5,
            ignoreDepthTest: true,
            ignoreDepthWrite: true,
        });
        cityIcon.rotation.set(-Math.PI / 2, 0, 0);
        teleportToCity.add(cityIcon);

        //room
        const teleportToHouse = new Teleporter(
            controls,
            new THREE.Vector3(100, 0, 501.5),
            rig,
            "Red"
        );
        const roomIcon = new Icon(controls, {
            src: "https://pivotal-5g-webxr.s3.amazonaws.com/assets/livingVr.png",
            color: "white",
            height: 0.5,
            width: 0.5,
            ignoreDepthTest: true,
            ignoreDepthWrite: true,
        });
        roomIcon.rotation.set(-Math.PI / 2, 0, 0);
        teleportToHouse.add(roomIcon);

        //animation
        const teleportToAnimated = new Teleporter(
            controls,
            new THREE.Vector3(-100, 0, -98),
            rig,
            "Orange"
        );
        const animateIcon = new Icon(controls, {
            src: "https://pivotal-5g-webxr.s3.amazonaws.com/assets/technology.png",
            color: appPrimaryColor,
            height: 0.5,
            width: 0.5,
            ignoreDepthTest: true,
            ignoreDepthWrite: true,
        });
        animateIcon.rotation.set(-Math.PI / 2, 0, 0);
        teleportToAnimated.add(animateIcon);

        console.log({ teleportToAnimated });

        //poles
        const teleportToPoles = new Teleporter(
            controls,
            new THREE.Vector3(-100, 0, 101),
            rig,
            "Orange"
        );
        const polesIcon = new Icon(controls, {
            src: "https://pivotal-5g-webxr.s3.amazonaws.com/assets/deployments.png",
            color: "white",
            height: 0.5,
            width: 0.5,
            ignoreDepthTest: true,
            ignoreDepthWrite: true,
        });
        polesIcon.rotation.set(-Math.PI / 2, 0, 0);
        teleportToPoles.add(polesIcon);

        rig.add(teleportToCity);
        rig.add(teleportToHouse);
        rig.add(teleportToAnimated);
        rig.add(teleportToPoles);

        teleportToAnimated.position.set(-1, 0.02, -0.75);
        teleportToCity.position.set(-0.35, 0.02, -0.75);
        teleportToPoles.position.set(0.3, 0.02, -0.75);
        teleportToHouse.position.set(0.9, 0.02, -0.75);

        teleportToCity.addEventListener("teleport", () => {
            resetColors();
            cityIcon.setColor(appPrimaryColor);

            lights.ambientLight.intensity = 0.125
            lights.hemiLight.intensity = 0.125
            $Scene.background = colorSky;
            $Scene.enviornment = colorSky;
        });
        teleportToHouse.addEventListener("teleport", () => {
            resetColors();
            roomIcon.setColor(appPrimaryColor);
            lights.ambientLight.intensity = 0.25
            lights.hemiLight.intensity = 0.125
            $Scene.background = skyTexture;
            $Scene.enviornment = skyTexture;
        });
        teleportToAnimated.addEventListener("teleport", () => {
            resetColors();
            animateIcon.setColor(appPrimaryColor);
            lights.ambientLight.intensity = 0.25
            lights.hemiLight.intensity = .125
            $Scene.background = colorSky;
            $Scene.enviornment = colorSky;
        });
        teleportToPoles.addEventListener("teleport", () => {
            resetColors();
            polesIcon.setColor(appPrimaryColor);
            lights.ambientLight.intensity = 0.125
            lights.hemiLight.intensity = 0.125
            $Scene.background = skyTexture;
            $Scene.enviornment = skyTexture;
        });

        const resetColors = () => {
            cityIcon.setColor("white");
            polesIcon.setColor("white");
            roomIcon.setColor("white");
            animateIcon.setColor("white");
        };

        // rig.position.set(100, 0, 501.5); //house
        rig.position.set(-100, 0, -98); //animated
        // rig.position.set(0, 0, .125); //city
    }

    function loadHouseScene(scene, controls) {
        const house = new HouseScene(controls);
        scene.add(house);
        house.position.set(100, -0.1, 500);
        house.addEventListener("loaded", () => {
            loadedHouse = true;
        });

        const geometry = new THREE.PlaneBufferGeometry(100, 100);
        const pMaterial = new THREE.MeshBasicMaterial({ color: "#2d2926" });
        const plane = new THREE.Mesh(geometry, pMaterial);
        plane.receiveShadow = false; //default
        plane.name = "floor";
        plane.rotation.set(Math.PI / -2, 0, 0);
        plane.position.set(0, 5, 0);
        house.add(plane);
    }

    function loadPoleScene(scene, controls) {
        const poles = new PolesScene(controls);
        scene.add(poles);
        poles.position.set(-100, 0, 100);
        const pMaterial = new THREE.MeshStandardMaterial({ color: "#2d2926" });
        const geometry = new THREE.PlaneBufferGeometry(100, 100);
        const plane = new THREE.Mesh(geometry, pMaterial);
        plane.receiveShadow = true; //default
        plane.name = "floor";
        plane.rotation.set(Math.PI / -2, 0, 0);
        plane.position.set(0, 0, 0);
        controls.intersectableObjects = [
            ...controls.intersectableObjects,
            plane,
        ];
        poles.add(plane);
    }

    function loadAnimatedScene(scene, controls) {
        const animated = new AnimatedScene(controls);

        animated.addEventListener("loaded", () => {
            loadedAnimation = true;
        });
        animated.position.set(-100, 0, -100);
        scene.add(animated);

        const geometry = new THREE.PlaneBufferGeometry(100, 100);
        const pMaterial = new THREE.MeshStandardMaterial({ color: "#2d2926" });
        const plane = new THREE.Mesh(geometry, pMaterial);
        plane.receiveShadow = true; //default
        plane.name = "floor";
        plane.rotation.set(Math.PI / -2, 0, 0);
        plane.position.set(0, 0, 0);
        controls.intersectableObjects = [
            ...controls.intersectableObjects,
            plane,
        ];
        animated.add(plane);

        animated.position.set(-100, 0, -100);
    }

    function loadLights(scene, controls) {
        const lights = new Lights(scene);
        return lights
    }

    function loadCityScene(scene, controls) {
        const city = new CityScene(controls, scene);
        scene.add(city);
        city.position.set(0, 0.5, -1.5);
        city.rotation.set(0, Math.PI / 3, 0);

        const pMaterial = new THREE.MeshStandardMaterial({ color: "#2d2926" });
        const geometry = new THREE.PlaneBufferGeometry(100, 100);
        const plane = new THREE.Mesh(geometry, pMaterial);
        plane.name = "floor";
        plane.rotation.set(Math.PI / -2, 0, 0);
        plane.position.set(0, 0, 0);
        plane.receiveShadow = true;
        controls.intersectableObjects = [
            ...controls.intersectableObjects,
            plane,
        ];
        scene.add(plane);

        city.addEventListener("loaded", () => {
            loadedCity = true;
        });
    }

    function loadControls({ scene, camera, renderer, rig }) {
        const controlOptions = {
            scene,
            camera,
            renderer,
            sceneContainer,
            xrOptions: { parent: rig },
        };

        const controls = new Controls(controlOptions);
        return controls;
    }

    function loadRenderer() {
        const renderer = new THREE.WebGLRenderer({ antialias: true, canvas });
        renderer.xr.enabled = true;
        renderer.setAnimationLoop(render);
        return renderer;
    }

    function render(time) {
        TWEEN.update();
        if ($Renderer) $Renderer.render($Scene, $Camera);
        try {
            $Scene.traverse((child) => {
                if (typeof child.tick === "function") child.tick();
            });
        } catch (e) {
            console.log(e);
        }
    }

    function resize() {
        if (!$Camera) return;
        $Camera.aspect = window.innerWidth / window.innerHeight;
        $Camera.updateProjectionMatrix();
        $Renderer.setSize(window.innerWidth, window.innerHeight);
    }

    async function enterVR() {
        if (!loadedBG) return;
        try {
            const sessionOptions = {
                optionalFeatures: [
                    "local-floor",
                    "bounded-floor",
                    "hand-tracking",
                    "layers",
                    'low-fixed-foveation-level',
                ],
            };
            const session = await navigator.xr.requestSession(
                "immersive-vr",
                sessionOptions
            );
            $Renderer.xr.setReferenceSpaceType("local-floor");
            $Renderer.xr.setSession(session);
            // $Renderer.xr.setFramebufferScaleFactor(0.75);
        } catch (e) {
            console.log(e);
        }
    }

    onMount(loadScene);

    $: handleLoaded(loadedBG, loadedCity, loadedAnimation, loadedHouse);

    const handleLoaded = () => {
        console.log({
            loadedBG,
            loadedCity,
            loadedAnimation,
        });
        if (!loadedBG) return;
        if (!loadedCity) return;
        if (!loadedAnimation) return;
        if (!loadedHouse) return;

        loaded = true;
    };
</script>

<svelte:window on:resize={resize} />
<div
    class="scene-wrapper"
    on:click={enterVR}
    style={`display: ${!loaded ? "none" : "default"}`}
>
    <canvas bind:this={canvas} id="scene-canvas" class="scene-canvas" />
</div>
{#if !loaded}
    <LoadingAnimation />
{/if}
<div id="beamForming" class="capture">
    {#if $animatedText === "paused"}
        <img src={Pivot} alt="pivot" />
        <div style="font-size: 30px; font-weight: bold">
            Pivot 5G network repeater extends gNB coverage and capacity beyond
            gNB line-of-site, reducing mmWave service TCO and deployment time.
            Ultra-low latency, automatic and agnostic — the smart choice
            repeater choice for carriers.
        </div>
    {:else}
        <h4 style="font-size: 60px">Holographic Beam Forming™</h4>
        <div style="font-size: 30px; font-weight: bold">
            Pivotal's Holographic Beam Forming technology gives its
            software-defined antennas the world's lowest cost, size, weight and
            power consumption (C-SWaP)
        </div>
    {/if}
</div>

<div id="bikeStand" class="capture">
    <h4 style="font-size: 120px">E-BIKE CHARGING STATION</h4>
</div>

<div id="wasteBin" class="capture">
    <h4 style="font-size: 120px">SMART WASTE BIN</h4>
</div>

<div id="streetLight" class="capture">
    <h4 style="font-size: 120px">CONVENTIONAL STREET LIGHT POLE</h4>
</div>

<div id="cityScene" class="capture">
    <div style="font-size: 90px">Subscribers Delivered:</div>
    <div style="font-size: 160px">{$subscribers}</div>
</div>

this.text = ['5gbox', 'ar', 'phone', 'headset']
<div id="houseScene" class="capture">
    {#if $houseText === "5gbox"}
        <div style="font-size: 60px">Gigabit Fixed Wireless Access</div>
        <div style="font-size: 40px; width: 80%">
            Gives consumers and alternative to cable monopoly and mobile network
            operators access to +$100B market
        </div>
        <div class="icons-row">
            <img src={Netflix} alt="netflix" style={"height: 70px"} />
            <img src={Prime} alt="prime" />
            <img src={Disney} alt="disney" />
        </div>
    {:else if $houseText === "headset"}
        <div style="font-size: 60px">Virtual Reality Headset</div>
        <div style="font-size: 45px">
            Virtual reality creates an immersive user experience. Echo 5G will
            deliver the mmWave indoors so intensive processing can be offloaded
            to the mobile edge or cloud.
        </div>
    {:else if $houseText === "phone"}
        <div style="font-size: 60px">Smartphone</div>
        <div style="font-size: 45px">
            Echo 5G bypasses WiFi and brings ultra-high speed and low-latency
            mmWave and mobile edge indoors direct to your smartphone.
        </div>
    {:else if $houseText === "ar"}
        <div style="font-size: 60px">Augmented Reality Hardware</div>
        <div style="font-size: 45px">
            Augmented reality overlays virtual information onto a live view of
            the real world. Echo 5G will deliver the mmWave indoors so intensive
            processing can be offloaded to the mobile edge or cloud.
        </div>
    {/if}
</div>
<div id="windowScene" class="capture">
    <img src={Echo} style="height: 150px" alt="echo" />
    <div style="font-size: 45px">
        Echo 5G is a subscriber-installed, on-the-window beamforming repeater
        designed to counteract mmWave penetration, reflection, and structural
        shadowing losses so it can gently flood an interior with mmWave signal.
    </div>
</div>

<style>
    div {
        font-weight: 700;
    }
    .scene-canvas {
        position: absolute;
        height: 100%;
        width: 100%;
    }

    .scene-wrapper {
        background-color: #1e1e1e;
        width: 100%;
        height: 100%;
        position: fixed;
        z-index: 1;
    }

    .capture {
        position: absolute;
        height: 500px;
        width: 1000px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 30px;
        text-align: center;
    }

    .icons-row {
        margin-top: 30px;
        display: flex;
        flex-direction: row;
        width: 100%;
        justify-content: space-around;
    }

    img {
        height: 100px;
        margin-bottom: 50px;
    }
</style>
